import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, CTABand } from "@/components/site/Blocks";
import { states } from "@/data/states";
import { buildHead } from "@/lib/seo-head";
import { GovernmentAffiliationNotice } from "@/components/site/GovernmentAffiliationNotice";
import heroDda from "@/assets/hero/hero-difficult-development-areas.jpg.asset.json";

const HERO = {
  src: heroDda.url,
  alt: "Tower crane lifting materials at a large apartment construction site at dusk, blueprints and cost sheets on a desk in the foreground, and glowing gold designated-area polygons over a city map — HUD Difficult Development Areas for LIHTC investors",
};

export const Route = createFileRoute("/difficult-development-areas/")({
  head: () => {
    const url = "/difficult-development-areas";
    const title = "2026 Difficult Development Areas (DDA) | Simply Approved";
    const description =
      "HUD's 2026 Difficult Development Areas — metro ZIPs and non-metro counties eligible for LIHTC's 30% basis boost. Pick a state to see every DDA.";
    return buildHead({
      title,
      description,
      url,
      keywords:
        "difficult development areas 2026, DDA by state, HUD DDA lookup, SDDA ZIP codes, LIHTC 30% basis boost DDA, non-metro DDA counties, HUD difficult development area map, 130% qualified basis DDA, LIHTC high cost area, affordable housing DDA designation, DDA vs QCT",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Difficult Development Areas", url },
      ],
      service: { name: "Difficult Development Area Lookup" },
      image: HERO.src,
      imageAlt: HERO.alt,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: "2026 Difficult Development Areas — All States",
          description,
          creator: { "@type": "GovernmentOrganization", name: "U.S. Department of Housing and Urban Development" },
          license: "https://www.huduser.gov/portal/datasets/qct.html",
          temporalCoverage: "2026",
        },
      ],
    });
  },
  component: DdaHub,
});

function DdaHub() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="HUD data"
        title="Difficult Development Areas (DDA)"
        subtitle="HUD designates DDAs where construction, land, and utility costs are high relative to AMI. Projects in a DDA qualify for LIHTC's 30% basis boost. Pick a state to see every 2026 SDDA ZIP and non-metro county DDA."
        primaryLabel="Start My Approval"
        primaryHref="/apply"
        secondaryLabel="See Today's Rates"
        secondaryHref="/rates"
        image={HERO}
      />
      <Section>
        <div className="mx-auto max-w-5xl">
          <h2 className="font-display text-2xl font-bold text-foreground">DDAs by state</h2>
          <ul className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {states.map((s) => (
              <li key={s.slug}>
                <Link
                  to="/difficult-development-areas/$state"
                  params={{ state: s.slug }}
                  className="block rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:border-brand hover:text-brand"
                >
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Section>
      <div className="container mx-auto max-w-7xl px-4 pb-10">
        <GovernmentAffiliationNotice />
      </div>

      <CTABand
        title="LIHTC or DDA project in the pipeline?"
        body="Send the address — we'll confirm DDA/QCT status, pull HUD FMR, and reply with DSCR pricing feedback."
      />
    </SiteShell>
  );
}
